<template>
  <div class="search-modal-body">
    <v-toolbar class="search-toolbar" density="comfortable" flat>
      <div class="search-bar">
        <v-btn
          icon
          variant="text"
          class="back-btn"
          aria-label="Back"
          @click="modal.close()"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="title-text text-left">Cancellation Options</div>
      </div>
    </v-toolbar>

    <v-card-text>
      <div class="option-stack">
        <button
          type="button"
          class="refund-option"
          :class="{ 'refund-option--selected': selectedOption === 'full' }"
          @click="selectedOption = 'full'"
        >
          <div class="refund-option-copy">
            <p class="refund-option-title">Cancel with full refund</p>
            <p class="refund-option-subtitle">Refund the customer the full amount.</p>
          </div>
          <div class="refund-option-amount">
            <v-icon size="18">mdi-cash-refund</v-icon>
            <span>{{ refundBaseDisplay }}</span>
          </div>
        </button>

        <button
          type="button"
          class="refund-option"
          :class="{ 'refund-option--selected': selectedOption === 'partial' }"
          @click="selectedOption = 'partial'"
        >
          <div class="refund-option-copy">
            <p class="refund-option-title">Cancel with partial refund</p>
            <p class="refund-option-subtitle">Refund a portion of the payment.</p>
          </div>
          <v-icon size="18">mdi-cash-minus</v-icon>
        </button>

        <div v-if="selectedOption === 'partial'" class="partial-field">
          <div>
            <div class="mb-2">
              <AppTextField
                v-model="partialRefundAmount"
                label="Partial refund amount"
                variant="outlined"
                type="number"
                min="0"
                step="0.01"
                prefix="$"
                placeholder="Enter refund amount"
              />
              <p v-if="partialRefundError" class="field-error">
                {{ partialRefundError }}
              </p>
            </div>
            <AppTextArea
              v-model="partialRefundReason"
              class=""
              label="Reason for partial refund"
              variant="outlined"
              rows="3"
              placeholder="Enter a short reason for the partial refund"
            />
            <p v-if="partialRefundReasonError" class="field-error">
              {{ partialRefundReasonError }}
            </p>
          </div>
        </div>

        <button
          type="button"
          class="refund-option"
          :class="{ 'refund-option--selected': selectedOption === 'none' }"
          @click="selectedOption = 'none'"
        >
          <div class="refund-option-copy">
            <p class="refund-option-title">Cancel without refund</p>
            <p class="refund-option-subtitle">No refund will be issued.</p>
          </div>
          <v-icon size="18">mdi-cash-remove</v-icon>
        </button>

        <button
          type="button"
          class="refund-option refund-option--reject"
          :class="{ 'refund-option--selected': selectedOption === 'reject' }"
          @click="selectedOption = 'reject'"
        >
          <div class="refund-option-copy">
            <p class="refund-option-title">Reject request</p>
            <p class="refund-option-subtitle">
              Decline the cancellation and keep the appointment active.
            </p>
          </div>
          <v-icon size="18">mdi-close-circle-outline</v-icon>
        </button>

        <div v-if="selectedOption === 'reject'" class="partial-field">
          <AppTextArea
            v-model="rejectReason"
            label="Reason for rejection"
            variant="outlined"
            rows="3"
            placeholder="Enter a short reason for rejecting the request"
          />
          <p v-if="rejectReasonError" class="field-error">{{ rejectReasonError }}</p>
        </div>
      </div>

      <p v-if="submitError" class="field-error field-error--submit">{{ submitError }}</p>
    </v-card-text>

    <div class="modal-actions">
      <AppButtonFlat
        size="small"
        color="primary"
        block
        :disabled="!canContinue || isSubmitting"
        :loading="isSubmitting"
        prepend-icon="mdi-check"
        style="font-size: 15px"
        @click="handleSubmit"
      >
        Submit
      </AppButtonFlat>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useModalStore } from "@/shared/stores/modal";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import { formatCurrency } from "@/shared/utils/utils";
import { cancelVendorAppointment } from "@/shared/services/appointments.api";

const props = defineProps<{
  appointment: Record<string, any>;
  onSuccess?: () => void | Promise<void>;
}>();

const modal = useModalStore();
const selectedOption = ref<"full" | "partial" | "none" | "reject">("full");
const partialRefundAmount = ref("");
const partialRefundError = ref("");
const partialRefundReason = ref("");
const partialRefundReasonError = ref("");
const rejectReason = ref("");
const rejectReasonError = ref("");
const submitError = ref("");
const isSubmitting = ref(false);

const refundBaseAmount = computed(() =>
  normalizeAmount(props.appointment?.total_info?.total_amount)
);
const refundBaseLabel = computed(() =>
  refundBaseAmount.value === null ? "" : formatCurrency(refundBaseAmount.value)
);
const refundBaseDisplay = computed(() => refundBaseLabel.value || "-");
const canContinue = computed(() => {
  if (selectedOption.value === "partial") {
    return (
      isValidPartialRefundAmount(partialRefundAmount.value) &&
      partialRefundReason.value.trim().length > 0
    );
  }
  if (selectedOption.value === "reject") {
    return rejectReason.value.trim().length > 0;
  }
  return true;
});

watch(selectedOption, () => {
  if (partialRefundError.value) partialRefundError.value = "";
  if (rejectReasonError.value) rejectReasonError.value = "";
});

watch(partialRefundAmount, () => {
  if (partialRefundError.value) partialRefundError.value = "";
});

watch(partialRefundReason, () => {
  if (partialRefundReasonError.value) partialRefundReasonError.value = "";
});

watch(rejectReason, () => {
  if (rejectReasonError.value) rejectReasonError.value = "";
});

async function handleSubmit() {
  if (
    selectedOption.value === "partial" &&
    !isValidPartialRefundAmount(partialRefundAmount.value)
  ) {
    partialRefundError.value = "Enter a valid partial refund amount.";
    return;
  }

  if (selectedOption.value === "partial" && !partialRefundReason.value.trim()) {
    partialRefundReasonError.value = "Enter a reason for the partial refund.";
    return;
  }

  if (selectedOption.value === "reject" && !rejectReason.value.trim()) {
    rejectReasonError.value = "Enter a reason for rejecting this request.";
    return;
  }

  if (isSubmitting.value) return;

  submitError.value = "";
  isSubmitting.value = true;

  try {
    await cancelVendorAppointment(props.appointment?.id, {
      refund_type: selectedOption.value,
      ...(selectedOption.value === "partial"
        ? {
            refund_amount: Number(partialRefundAmount.value),
            reason: partialRefundReason.value.trim(),
          }
        : {}),
      ...(selectedOption.value === "reject" ? { reason: rejectReason.value.trim() } : {}),
    });

    await props.onSuccess?.();
    modal.close();
  } catch (error: any) {
    submitError.value =
      error?.response?.data?.message || error?.message || "Failed to cancel appointment.";
    // eslint-disable-next-line no-console
    console.error("cancelVendorAppointment failed", error);
  } finally {
    isSubmitting.value = false;
  }
}

function normalizeAmount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isValidPartialRefundAmount(value: string): boolean {
  const amount = Number(value);
  const base = refundBaseAmount.value;
  if (!Number.isFinite(amount) || amount <= 0) return false;
  if (base === null) return false;
  return amount <= base;
}
</script>

<style scoped>
.search-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.search-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
}

:deep(.search-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 10px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-body {
  padding: 16px 16px 8px;
}

.modal-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.option-stack {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.refund-summary,
.partial-field {
  border-radius: 12px;
  background: #f8fafc;
}

.refund-summary--highlight {
  border-color: #f97316;
  background: #fff7ed;
}

.refund-summary-label {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9a3412;
}

.refund-summary-value {
  margin: 4px 0 0;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.refund-summary-helper {
  margin: 6px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.refund-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #fff;
  text-align: left;
}

.refund-option-amount {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 800;
  color: #9a3412;
  flex-shrink: 0;
}

.refund-option--selected {
  border-color: #f97316;
  background: #fff7ed;
}

.refund-option--reject.refund-option--selected {
  border-color: #ef4444;
  background: #fef2f2;
}

.refund-option-copy {
  min-width: 0;
}

.refund-option-title {
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.refund-option-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
}

.modal-actions {
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 12px;
  position: fixed;
  bottom: 0;
  background-color: #fff;
  border-top: 1px solid #e2e8f0;
}

.field-error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #dc2626;
}

.field-error--submit {
  margin-top: 12px;
}
</style>
